/* eslint-disable import/prefer-default-export */
import { cloneDeep } from 'lodash'
import MeasureType, { MeasureTypeStrings } from 'enums/MeasureType'
import MetricCategoryItem, { MetricCategoryItemStrings } from 'enums/MetricCategoryItem'
import { MetricSeriesStrings } from 'enums/MetricSeries'
import { MetricSeries } from 'schemas/MetricSeriesSchema'
import * as measureTypeUtil from 'utils/measureTypeUtil'
import { AnalysisTypeStrings } from 'enums/AnalysisType'
import ReturnStatus from 'enums/ReturnStatus'
import { MessageConst } from 'enums'
import InsightViewOption, { InsightViewOptionStrings } from 'enums/InsightViewOption'
import {
  ChartConfig,
  Delta
} from 'components/visualization/MetricGrid/MetricItem/types/chartConfig'
import { ChartType } from 'components/visualization/MetricGrid/MetricItem/types/chartType'
import { getChartType } from 'utils/summaryUtil'
import { getChildTypes } from 'utils/MetricItemUtil'
import { checkValidDelta, formatDeltaByMeasureType } from 'utils/measureTypeUtil'
import { genAxis, genData, genSeriesItem } from './ChartHandler'

// TODO: VoC related
// if (nb.dto.ViewOption.isMeasurementType(this.view)

const getTotalKey = () => MetricCategoryItem.NONE
const getSumKey = () => MetricCategoryItem.SUM

const getNearUpperBound = (value: number) => {
  if (typeof value !== 'number') {
    return 0
  }
  const upperBounds = [1, 2, 5]
  let index = 0
  let multiple = 10
  let upper = upperBounds[index] * multiple

  while (upper < value) {
    index = (index + 1) % upperBounds.length
    if (index === 0) {
      multiple *= 10
    }
    upper = upperBounds[index] * multiple
  }

  return upper
}

const isObject = (value: any) => !!value && value.constructor.name === 'Object'

const getMax = (children: Record<string, any>) => {
  let max = 0
  const totalKey = getTotalKey()
  const sumKey = getSumKey()
  if (isObject(children)) {
    Object.entries(children).forEach(([key, value]) => {
      if (key !== totalKey && key !== sumKey && typeof value === 'number') {
        max = Math.max(max, value)
      }
    })
  }
  return max
}

const isNumber = (value: any) => typeof value === 'number'

const getFullLabel = (
  type: MeasureTypeStrings | MetricSeriesStrings | MetricCategoryItemStrings,
  analysisType?: AnalysisTypeStrings
) => measureTypeUtil.getLabel(type, analysisType)

const hasCategoryItem = (type: MeasureTypeStrings) =>
  [
    MeasureType.MENTIONS,
    MeasureType.UNIQUE_AUTHORS,
    MeasureType.OVERALL_STAR_RATINGS,
    MeasureType.OVERALL_SURVEY_SCORES,
    MeasureType.NET_PROMOTER_SCORES,
    MeasureType.STAR_RATINGS,
    MeasureType.SURVEY_SCORES
  ].includes(type)

const applyUnavailable = (
  data: Record<string, Record<string, string | number>>,
  types: string[],
  item: string
) => {
  const UnavailableAppliedData = cloneDeep(data)
  types.forEach((type) => {
    if (UnavailableAppliedData?.[type]) {
      if (UnavailableAppliedData[type][item] === undefined) {
        UnavailableAppliedData[type][item] = MessageConst.NOT_AVAILABLE_ABBR
      } else if (UnavailableAppliedData[type][item] !== MessageConst.NOT_AVAILABLE_ABBR) {
        UnavailableAppliedData[type][item] = MessageConst.EM_DASH
      }
    }
  })
  return UnavailableAppliedData
}

const handleAvailable = (
  data: Record<string, Record<string, string | number>>,
  analysisType: AnalysisTypeStrings,
  viewOption: InsightViewOptionStrings,
  isProjectedTopic: boolean
) => {
  let processedData = cloneDeep(data)
  const checkType = getFullLabel(MeasureType.MENTIONS, analysisType)

  if (processedData && processedData[checkType!]) {
    // CB-20555
    const noDataCheckTypes = [
      MeasureType.IMPRESSIONS,
      MeasureType.NET_SENTIMENT,
      MeasureType.PASSION_INTENSITY,
      MeasureType.ENGAGEMENTS,
      MeasureType.ENGAGEMENT_RATIO
    ].map((type) => getFullLabel(type, analysisType)!)

    Object.keys(processedData[checkType!]).forEach((item) => {
      if (item !== MetricCategoryItem.NONE && processedData[checkType!][item] === 0) {
        processedData = applyUnavailable(processedData, noDataCheckTypes, item)
      }
    })
  }

  // CB-20525
  if (viewOption === InsightViewOption.CHANNELS) {
    // Engagement Currently only Support Facebook, Twitter, Youtube, Instagram and TikTok
    const msTypes = [
      MeasureType.ENGAGEMENTS,
      MeasureType.ENGAGEMENT_RATIO,
      MeasureType.COMMENTS,
      MeasureType.LIKES,
      MeasureType.SHARES,
      MeasureType.DISLIKES,
      MeasureType.VIEWS
    ].map((type) => getFullLabel(type, analysisType))

    msTypes.forEach((type) => {
      if (processedData[type!]) {
        // CB-22500, Also support Twitter now when firehose.
        if (isProjectedTopic) {
          processedData[type!][MetricCategoryItem.TWITTER] = MessageConst.EM_DASH
        }
        if (type === MeasureType.SHARES) {
          processedData[type!][MetricCategoryItem.INSTAGRAM] = MessageConst.EM_DASH
          processedData[type!][MetricCategoryItem.YOU_TUBE] = MessageConst.EM_DASH
          processedData[type!][MetricCategoryItem.OTHER] = MessageConst.EM_DASH
        } else if (type === MeasureType.DISLIKES || type === MeasureType.VIEWS) {
          processedData[type!][MetricCategoryItem.FACEBOOK] = MessageConst.EM_DASH
          processedData[type!][MetricCategoryItem.INSTAGRAM] = MessageConst.EM_DASH
        } else if (type === MeasureType.LIKES) {
          processedData[type!][MetricCategoryItem.OTHER] = MessageConst.EM_DASH
        }
      }
    })
  }

  return processedData
}

export const extractData = (
  seriesList: MetricSeries[],
  isDelta: boolean,
  analysisType: AnalysisTypeStrings,
  isProjectedTopic: boolean,
  viewOption: InsightViewOptionStrings,
  opt_isMetricName?: boolean
) => {
  if (!seriesList) return {}
  let data: Record<string, Record<string, string | number>> = {}
  seriesList.forEach((seriesItem) => {
    const { seriesName, categoryItem, set, status } = seriesItem

    // Derek's TODO: seriesNameKey should use enums key instead of display string
    const seriesNameKey = opt_isMetricName ? seriesName : getFullLabel(seriesName!, analysisType)
    data[seriesNameKey!] = data[seriesNameKey!] ?? {}

    const item = categoryItem || getTotalKey()
    if (status !== ReturnStatus.LANGUAGE_NOT_SUPPORTED) {
      // eslint-disable-next-line prefer-destructuring
      data[seriesNameKey!][item] = set![0]!
    } else if (!isDelta) {
      data[seriesNameKey!][item] = MessageConst.NOT_AVAILABLE_ABBR
    }
  })
  if (!isDelta) {
    data = handleAvailable(data, analysisType, viewOption, isProjectedTopic)
  }

  return data
}

const handleOverviewChildren = (
  chartConfig: ChartConfig,
  metricData: Record<string, Record<string, string | number>>,
  childTypes: any[],
  analysisType: AnalysisTypeStrings
) => {
  const chartConfigResult = cloneDeep(chartConfig)
  const data: any[] = []
  const totalKey = getTotalKey()
  if (chartConfigResult.type === ChartType.SPEED_DIAL) {
    // guage chart: only legendData is used in v4
    if (childTypes?.length > 0) {
      // CB-20113, O+E | Summary Metrics Overview - Net Sentiment Metric
      childTypes.forEach((type) => {
        const label = getFullLabel(type, analysisType)
        data.push(genData(label!, metricData[label!][totalKey]))
      })
      chartConfigResult.legendData = genSeriesItem('', data)
    } else {
      chartConfigResult.showLegend = false
    }
  } else if (chartConfigResult.type === ChartType.DONUT) {
    // donut chart: only seriesData is used in v4
    if (childTypes?.length > 0) {
      if (hasCategoryItem(chartConfigResult.category)) {
        childTypes.forEach((type) => {
          const label = getFullLabel(type)
          data.push(genData(label!, metricData[chartConfigResult.name!][type]))
        })
      } else {
        childTypes.forEach((type) => {
          const label = getFullLabel(type, analysisType)
          data.push(genData(label!, metricData[label!][totalKey]))
        })
      }
    } else {
      chartConfigResult.showLegend = false
      data.push(genData(chartConfigResult.name!, chartConfigResult.value!))
    }
    chartConfigResult.seriesData?.push(genSeriesItem('', data))
  }
  return chartConfigResult
}

const handleOPEChildren = (
  chartConfig: ChartConfig,
  metricData: Record<string, Record<string, string | number>>,
  childTypes: any[]
) => {
  const chartConfigResult = cloneDeep(chartConfig)
  const data: any[] = []
  const legendData: any[] = []

  if (chartConfigResult.type === ChartType.SPEED_DIAL) {
    // guage chart: only legendData is used in v4
    childTypes.forEach((type) => {
      const value = metricData[chartConfigResult.name!][type]
      // Handle legend data.
      legendData.push(genData(getFullLabel(type)!, value))
    })
    chartConfigResult.legendData = genSeriesItem('', legendData)
  } else if (chartConfigResult.type === ChartType.DONUT) {
    // donut chart: only seriesData is used in v4
    childTypes.forEach((type) => {
      const value = metricData[chartConfigResult.name!][type]
      data.push(genData(getFullLabel(type)!, value))
    })
    chartConfigResult.seriesData!.push(genSeriesItem('', data))
  }
  return chartConfigResult
}

const handleChannelsChildren = (
  chartConfig: ChartConfig,
  metricData: Record<string, Record<string, string | number>>,
  childTypes: any[]
) => {
  const chartConfigResult = cloneDeep(chartConfig)
  if (
    chartConfigResult.type === ChartType.ROUND_BAR ||
    chartConfigResult.type === ChartType.SCALE_BAR
  ) {
    childTypes.forEach((type) => {
      const value = isNumber(metricData[chartConfigResult.name!][type])
        ? metricData[chartConfigResult.name!][type]
        : 0
      const displayValue = isNumber(metricData[chartConfigResult.name!][type])
        ? undefined
        : MessageConst.EM_DASH
      chartConfigResult.seriesData!.push(
        genSeriesItem(getFullLabel(type!)!, [genData('', value)], displayValue)
      )
    })
  }
  return chartConfigResult
}

const handleChildren = (
  chartConfig: ChartConfig,
  metricData: Record<string, Record<string, string | number>>,
  analysisType: AnalysisTypeStrings,
  viewOption: InsightViewOptionStrings
) => {
  let chartConfigResult = {} as ChartConfig
  const childTypes = getChildTypes(analysisType, viewOption, chartConfig.category)
  switch (viewOption) {
    case InsightViewOption.STAR_RATINGS:
    case InsightViewOption.SURVEY_SCORES:
      // TODO: VoC
      break
    case InsightViewOption.OWNED_PARTNERED_EARNED:
      chartConfigResult = handleOPEChildren(chartConfig, metricData, childTypes)
      break
    case InsightViewOption.CHANNELS:
      chartConfigResult = handleChannelsChildren(chartConfig, metricData, childTypes)
      break
    default:
      chartConfigResult = handleOverviewChildren(chartConfig, metricData, childTypes, analysisType)
      break
  }
  return chartConfigResult
}

// generating chartConfig
export const parseChart = (
  category: MeasureTypeStrings,
  seriesName: string,
  metricData: Record<string, Record<string, string | number>>,
  deltaData: Record<string, Record<string, string | number>>,
  viewOption: InsightViewOptionStrings,
  analysisType: AnalysisTypeStrings
): ChartConfig => {
  const totalKey = getTotalKey()
  const chartType = getChartType(category, viewOption)

  const chartConfig: ChartConfig = {
    type: chartType,
    category,
    name: seriesName,
    label: seriesName,
    value: metricData[seriesName][totalKey],
    seriesData: [],
    showLegend: true
  }
  // only gauge chart need axis
  if (chartConfig.type === ChartType.SPEED_DIAL) {
    if (category === MeasureType.PASSION_INTENSITY) {
      chartConfig.axis = { min: 0, max: 100 }
    } else if (category === MeasureType.ENGAGEMENT_RATIO) {
      // Dynamic upper bound for engagement per post metric.
      chartConfig.axis = genAxis(
        0,
        getNearUpperBound(
          Math.max(chartConfig.value as number, getMax(metricData[chartConfig.category]))
        )
      )
    } else {
      chartConfig.axis = genAxis(-1, 1)
    }
  }

  // Handle children types.
  const chartConfigResult = handleChildren(chartConfig, metricData, analysisType, viewOption)

  // change total value to dash if all children are dash in domain
  if (
    chartConfigResult.type === ChartType.SCALE_BAR ||
    chartConfigResult.type === ChartType.ROUND_BAR
  ) {
    const isChildrenAllDash = chartConfigResult.seriesData.every(
      (seriesItem) => seriesItem.displayValue === MessageConst.EM_DASH
    )
    if (isChildrenAllDash && chartConfigResult.value !== MessageConst.NOT_AVAILABLE_ABBR) {
      chartConfigResult.value = MessageConst.EM_DASH
    }
  }

  // Handle delta value.
  if (deltaData[seriesName]) {
    const deltaVal = deltaData[seriesName][totalKey]

    // Handle delta direction.
    let deltaDirection: Delta['direction'] = 'none'
    if (chartConfig.value === deltaVal) {
      deltaDirection = 'none'
    } else if (chartConfig.value! > deltaVal) {
      deltaDirection = 'up'
    } else {
      deltaDirection = 'down'
    }

    if (isNumber(chartConfig.value) && isNumber(deltaVal)) {
      const isValidDelta = checkValidDelta(category, deltaVal! as number)
      chartConfigResult.delta = isValidDelta
        ? {
            value: formatDeltaByMeasureType(
              category,
              chartConfig.value as number,
              deltaVal as number
            ),
            direction: deltaDirection
          }
        : {
            value: MessageConst.EM_DASH,
            direction: 'none'
          }
    }
  }
  return chartConfigResult
}
